import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { useSelector } from "react-redux";
import ModernEmployeeSelector from "../../../components/utils/Selector/ModernEmployeeSelector";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";
import { AbsenceStatsSection } from "../../../components/dashboard/absences/absence-stats-section";
import { AbsencesStacklist } from "../../../components/dashboard/absences/absences-stacklist";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { startOfYear, endOfYear, format } from "date-fns";
import { downloadAbsenceAvspasseringDebugExport, exportAbsenceSummaryPDF } from "../../../api/absence/absenece";
import { fetchUsers } from "../../../api/users";
import { CodeBracketIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

export const AbsenceOverview = () => {
  const { t } = useTranslation();
  // Check if user can view absences (admin access)
  const isAdmin = useUserHasAccess({ role: "absence", action: "read" });
  const currentUser = useSelector((state) => state.session.currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial values from URL params or defaults
  // For admin: allow viewing any user (null means show selector)
  // For non-admin: always use current user
  const initialUserId = searchParams.get("user_id") || (isAdmin ? null : currentUser?.id);
  const initialFrom = searchParams.get("from") || format(startOfYear(new Date()), "yyyy-MM-dd");
  const initialTo = searchParams.get("to") || format(endOfYear(new Date()), "yyyy-MM-dd");
  
  // Initialize user state - if we have a user_id from URL, use it, otherwise use currentUser
  const [user, setUser] = useState(() => {
    if (initialUserId) {
      return { id: parseInt(initialUserId) };
    }
    return currentUser;
  });
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingDebug, setIsExportingDebug] = useState(false);
  const [period, setPeriod] = useState({
    periodType: "year",
    from: initialFrom,
    to: initialTo,
  });

  // Update URL params when user or period changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (user?.id) {
      params.set("user_id", user.id.toString());
    }
    if (period.from) {
      params.set("from", period.from);
    }
    if (period.to) {
      params.set("to", period.to);
    }
    if (period.periodType) {
      params.set("periodType", period.periodType);
    }
    setSearchParams(params, { replace: true });
  }, [user, period, setSearchParams]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchUsers({
      page: 1,
      per_page: 200,
      sortBy: "name",
      sort: "asc",
      fields: "id,name,displayTitle",
      filters: [{ name: "EMPLOYEE_OR_HIRED", value: true }],
    })
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setEmployeeOptions(
          list.map((u) => ({
            value: String(u.id),
            label: u.displayTitle || u.name || String(u.id),
          }))
        );
      })
      .catch(() => setEmployeeOptions([]));
  }, [isAdmin]);

  const moduleFilterInitial = useMemo(
    () => ({
      query: "",
      users: user?.id ? [String(user.id)] : [],
    }),
    [user?.id]
  );

  // Fetch user details if we only have ID from URL
  useEffect(() => {
    if (initialUserId && (!user?.displayTitle || user?.id !== parseInt(initialUserId))) {
      // User ID is set but we don't have full user object - fetch it
      import("../../../api/users").then(({ fetchUser }) => {
        fetchUser(parseInt(initialUserId), { fields: "id,name,displayTitle,email" })
          .then((response) => {
            if (response?.data) {
              setUser(response.data);
            }
          })
          .catch((error) => {
            console.error("Error fetching user:", error);
          });
      });
    }
  }, [initialUserId]);

  const content = (
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          {/* Period Selector */}
          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
            <div className="bg-white px-4 py-5 sm:px-6">
              <PeriodSelector
                periodType={period.periodType}
                from={period.from}
                to={period.to}
                onChange={(newPeriod) => {
                  setPeriod(newPeriod);
                }}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white p-4 shadow-sm">
            {isAdmin ? (
              <ModuleFilter
                filterCard={false}
                hideStatusFilter
                hideSearch
                usersFilterLabel={t("absence.overview.employee")}
                userOptions={employeeOptions}
                initialFilters={moduleFilterInitial}
                onFiltersChange={(mf) => {
                  const nextId = mf?.users?.[0];
                  if (!nextId) return;
                  if (Number(nextId) === Number(user?.id)) return;
                  const match = employeeOptions.find((o) => String(o.value) === String(nextId));
                  setUser({
                    id: Number(nextId),
                    displayTitle: match?.label,
                  });
                }}
              />
            ) : (
              <p className="text-sm font-medium text-slate-800">
                {user?.displayTitle || currentUser?.displayTitle}
              </p>
            )}
            {selectorOpen ? (
              <ModernEmployeeSelector
                open={selectorOpen}
                selectedEmployees={user && user.id ? [user] : []}
                onChange={(employees) => {
                  setUser(employees[0] || currentUser);
                  setSelectorOpen(false);
                }}
                onClose={() => setSelectorOpen(false)}
                multiple={false}
                filters={[{ name: "EMPLOYEE", value: true }]}
              />
            ) : null}
          </div>

          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
            <AbsenceStatsSection user={user} period={period} />
            <div className="mt-5" />
            <AbsencesStacklist user={user} period={period} />
          </div>
        </div>
      </div>
  );

  const handleExportPDF = async () => {
    if (isExportingPDF) return; // Prevent multiple clicks
    
    setIsExportingPDF(true);
    try {
      const params = {
        user_id: user?.id,
        from: period.from,
        to: period.to,
      };
      
      const result = await exportAbsenceSummaryPDF(params);
      
      if (result.success && result.data) {
        // Check if result.data is a Blob
        let blob;
        if (result.data instanceof Blob) {
          blob = result.data;
        } else {
          // If it's not a Blob, try to create one
          blob = new Blob([result.data], { type: 'application/pdf' });
        }
        
        // Validate PDF blob
        if (blob.size < 100) {
          throw new Error(t("absence.overview.pdf_too_small"));
        }
        
        // Create blob URL and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `fraværsoversikt_${user?.id || 'alle'}_${period.from}_${period.to}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success(t("absence.overview.pdf_exported"));
      } else {
        const errorMsg = result.errors?.join(", ") || t("absence.overview.pdf_export_failed");
        toast.error(errorMsg);
        console.error("PDF export error:", result);
      }
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error(error.message || t("absence.overview.pdf_export_failed"));
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleExportDebug = async () => {
    if (isExportingDebug || !user?.id) return;
    setIsExportingDebug(true);
    try {
      await downloadAbsenceAvspasseringDebugExport({
        userId: user.id,
        from: period.from,
        to: period.to,
      });
      toast.success(t("absence.overview.debug_exported"));
    } catch (error) {
      toast.error(error.message || t("absence.overview.debug_export_failed"));
    } finally {
      setIsExportingDebug(false);
    }
  };

  const headerActions = [
    {
      component: null,
      label: isExportingPDF ? t("absence.overview.exporting") : t("absence.overview.export_pdf"),
      description: t("absence.overview.export_hint"),
      path: null,
      action: handleExportPDF,
      icon: DocumentArrowDownIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: false,
      disabled: isExportingPDF,
    },
    {
      component: null,
      label: isExportingDebug ? t("absence.overview.debug_exporting") : t("absence.overview.debug_export"),
      description: t("absence.overview.debug_export_hint"),
      path: null,
      action: handleExportDebug,
      icon: CodeBracketIcon,
      textColor: "text-slate-700",
      bgColor: "bg-white",
      hidden: !user?.id,
      menu: false,
      disabled: isExportingDebug,
    },
  ];

  return (
    <>
      <DashboardHeader variant="list" subtitle="" title={t("absence.overview.title")} actions={headerActions}>
        {content}
      </DashboardHeader>
    </>
  );
};
